import { CSSProperties, MouseEvent, useMemo } from 'react';
import { To } from 'react-router-dom';
import {
    ColumnDef,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    Row,
    useReactTable,
    VisibilityState,
} from '@tanstack/react-table';

import { DEFAULT_PAGE_SIZE_OPTIONS } from '../types';
import { createTableStateChangeHandler, getStoredColumnSizes } from '../utils';
import { getColumnDefIds } from '../utils/getColumnDefIds';
import { mergeColumnOrder } from '../utils/mergeColumnOrder';
import { useTableState } from './useTableState';

export type TUseTableArgs<RowData extends object> = {
    columns: ColumnDef<RowData, any>[];
    data: RowData[];

    // Enable manual filtering, sorting and pagination
    manualFiltering?: boolean;
    manualSorting?: boolean;
    manualPagination?: boolean;

    columnsVisibility?: Partial<Record<keyof RowData, boolean>>;

    pageSizeOptions?: number[];
    pagination?: boolean;
    bordered?: boolean;
    loading?: boolean;
    fetching?: boolean;
    fixedLayout?: boolean;
    filterBar?: boolean;
    label?: string;
    dataCount?: number;
    dense?: boolean;

    // Name or id of table to store column sizes
    storeColumnSizes?: string;

    // Name or id of table to store the selected page size
    storePageSize?: string;

    // Choose to save pagination state in query params
    paginateByQuery?: boolean;

    // Callback to set row styles in each row
    rowStyle?: (row: RowData) => CSSProperties;
    onRowClick?: (row: RowData, event: MouseEvent<HTMLTableRowElement>) => void;

    // Renders every cell of the row as a link to the returned target, so rows can be opened
    // in a new tab/window with the native browser gestures (cmd/ctrl-click, middle-click,
    // context menu). Columns opting out set `disableRowLink` in their meta.
    rowLink?: (row: RowData) => To;
    state?: ReturnType<typeof useTableState>;

    // Map a row to a stable id (e.g. entity primary key). Used to key rowSelection
    // by the entity id rather than the row index.
    getRowId?: (row: RowData, index: number) => string;

    // Whether a row can be selected at all. Rows that cannot are rendered with a disabled
    // checkbox and are left out of the select-all.
    enableRowSelection?: boolean | ((row: Row<RowData>) => boolean);
};

export const useTable = <RowData extends object>({
    columns,
    data,

    state,

    manualFiltering,
    manualPagination,
    manualSorting,

    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    paginateByQuery,
    columnsVisibility,

    storeColumnSizes,
    storePageSize,
    dataCount,
    dense,
    bordered,
    fetching,
    fixedLayout,
    filterBar,
    label,
    loading,
    onRowClick,
    rowLink,
    rowStyle,
    pagination,
    getRowId,
    enableRowSelection,
}: TUseTableArgs<RowData>) => {
    const innerState = useTableState({
        pageSizeOptions,
        paginateByQuery,
        storeColumnSizes,
        storePageSize,
    });
    const finalState = state ?? innerState;
    // `search` is ours, not a react-table state key, so it must not reach useReactTable
    const {
        setColumnFilters,
        setSorting,
        setPagination,
        setRowSelection,
        setColumnSizing,
        setColumnVisibility,
        setColumnOrder,
        search,
        setSearch,
        ...tableState
    } = finalState;

    const columnOrder = useMemo(
        () => mergeColumnOrder(tableState.columnOrder, getColumnDefIds(columns)),
        [tableState.columnOrder, columns],
    );
    const columnVisibility = useMemo(
        () => ({ ...tableState.columnVisibility, ...(columnsVisibility as VisibilityState) }),
        [tableState.columnVisibility, columnsVisibility],
    );

    const getPageCount = () => {
        return dataCount ? Math.ceil(dataCount / tableState.pagination.pageSize) : 1;
    };

    const table = useReactTable({
        columns: columns,
        data,

        getRowId,
        enableRowSelection,

        columnResizeMode: 'onChange',

        // Setup row models
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),

        // We can decide to handle filtering, sorting and pagination manually
        manualFiltering,
        manualSorting,
        manualPagination,
        // Internal states for table (controlled)
        state: {
            ...tableState,
            columnOrder,
            columnVisibility,
        },
        initialState: {
            columnSizing: getStoredColumnSizes(storeColumnSizes),
        },
        enableColumnResizing: true,
        pageCount: manualPagination ? getPageCount() : undefined,

        // Handle controlled state for column filters
        onColumnFiltersChange: createTableStateChangeHandler(setColumnFilters),
        // Handle controlled state for sorting
        onSortingChange: createTableStateChangeHandler(setSorting),
        // Handle controlled state for pagination
        onPaginationChange: createTableStateChangeHandler(setPagination),
        // Handle controlled state for row selection
        onRowSelectionChange: createTableStateChangeHandler(setRowSelection),
        // Handle controlled state for column sizing
        onColumnSizingChange: createTableStateChangeHandler(setColumnSizing),
        onColumnVisibilityChange: createTableStateChangeHandler(setColumnVisibility),
        onColumnOrderChange: createTableStateChangeHandler(setColumnOrder),
    });

    return {
        ...table,
        state: { ...tableState, search, setSearch },
        props: {
            bordered,
            fetching,
            dense,
            filterBar,
            label,
            loading,
            onRowClick,
            rowLink,
            fixedLayout,
            rowStyle,
            pagination,
            storeColumnSizes,
            pageSizeOptions,
            dataCount,
        },
    };
};
